import { Link } from "react-router-dom";

/**
 * Shared layout for legal pages, consistent typography & spacing.
 * Provides a hero, table-of-contents, and prose container.
 */
export default function LegalPage({ eyebrow, title, lastUpdated, summary, sections }) {
  return (
    <>
      <section className="container-x pt-24 md:pt-32 pb-12" data-testid={`legal-hero-${eyebrow.toLowerCase()}`}>
        <div className="overline">{eyebrow}</div>
        <h1 className="mt-6 max-w-4xl font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl text-balance">
          {title}
        </h1>
        <p className="mt-6 text-zinc-500 text-sm">Last updated: {lastUpdated}</p>
        {summary && (
          <p className="mt-8 max-w-3xl text-zinc-300 leading-relaxed text-lg">
            {summary}
          </p>
        )}
      </section>

      <section className="section-y" data-testid={`legal-body-${eyebrow.toLowerCase()}`}>
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* TOC */}
          <aside className="lg:col-span-3 lg:sticky lg:top-28 self-start" data-testid="legal-toc">
            <div className="overline">Contents</div>
            <ul className="mt-6 space-y-3">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-sm text-zinc-400 hover:text-gold transition-colors"
                  >
                    <span className="text-zinc-600 mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-10 border-l border-gold/30 pl-4">
              <div className="overline">Need to reach us?</div>
              <Link to="/contact" className="block mt-3 text-sm text-zinc-300 hover:text-gold">
                Contact Toulios →
              </Link>
            </div>
          </aside>

          {/* Body */}
          <div className="lg:col-span-9 space-y-16">
            {sections.map((s, i) => (
              <article key={s.id} id={s.id} className="scroll-mt-28" data-testid={`legal-section-${s.id}`}>
                <div className="flex items-baseline gap-4">
                  <span className="overline text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="text-2xl md:text-3xl font-display font-light tracking-tight">
                    {s.title}
                  </h2>
                </div>
                <div className="mt-6 space-y-5 text-zinc-300 leading-relaxed text-[15px] max-w-3xl">
                  {s.body}
                </div>
              </article>
            ))}

            <div className="border-t border-white/[0.06] pt-10 text-xs text-zinc-500 leading-relaxed max-w-3xl">
              These policies are provided in good faith and are intended as a
              clear, accessible summary of how we operate. They do not constitute
              legal advice. If a clause conflicts with applicable Australian law,
              the law prevails.
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
